import {Moment} from 'moment';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {EntityUserCareTasksRepository} from '../../../../../../../repositories/entity-user-care-tasks';
import {
  transformEntityAnniversaries2PlannerAnniversaryItems,
  transformEntityCareTasks2PlannerActivityItems,
} from './planner-data-transformation';
import {DateRangeType} from '../../../../../../../components/date-range-input/date-range-utils';
import {PlannerActivityUser, PlannerItem} from './planner-item';
import {wait} from '../../../../../../../utils/functions/wait';
import {useSnackBar} from '../../../../../../../contexts/snack-bar/context';
import {translations} from '../../../../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {AffiliatedUser} from '../../../../../../../backend/responses/affiliated-user';
import {UserType} from '../../../../../../../backend/enums/user-type';
import {
  TaskStatus,
  UserFilters,
} from '../../../../../../../components/users-filters/users-filters-utils';
import {EntitiesRepository} from '../../../../../../../repositories/entities';
import {BIRTHDAY_SECTOR_ID} from '../side-panel/calendar-side-activities-sectors-filter';
import {
  FetchEntityUsersResult,
  useFetchEntityUsers,
} from '../../../../../../../hooks/use-fetch-entity-users';
import {EntityShiftAllocation} from '../../../../../../../backend/interfaces/entity-shift-allocation';
import {EntityShiftsAllocationsRepository} from '../../../../../../../repositories/entity-shifts-allocations';

export type PlannerFetchData = {
  fetching: boolean;
  items: PlannerItem[];
  usersData: FetchEntityUsersResult;
  caregiversData: FetchEntityUsersResult;
  refresh: () => void;
  filterDataByActivitiesGroups: (filterGroupsIds: string[] | undefined) => void;
  filterDataByUser: (user: AffiliatedUser | undefined) => void;
  filterDataByCaregiver: (caregiver: AffiliatedUser | undefined) => void;
  filterDataByUserFilters: (filters: UserFilters) => void;
};
export function usePlannerFetchData(opts: {
  entityId: string;
  startDate: Moment;
  endDate: Moment;
  range: DateRangeType;
}): PlannerFetchData {
  const [items, setItems] = useState<PlannerItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<PlannerItem[]>([]);
  const [filterActivitiesGroups, setFilterActivitiesGroups] = useState<string[]>();
  const [filterUser, setFilterUser] = useState<AffiliatedUser | undefined>();
  const [filterCaregiver, setFilterCaregiver] = useState<AffiliatedUser | undefined>();
  const [filters, setFilters] = useState<UserFilters>({});
  const [shiftAllocations, setShiftAllocations] = useState<EntityShiftAllocation[]>();
  const cacheUsers = useRef<Record<string, AffiliatedUser>>({});
  const [fetching, setFetching] = useState(true);
  const snackBar = useSnackBar();
  const {t} = useTranslation();
  const abort = useRef<AbortController | undefined>();
  const timeout = useRef<NodeJS.Timeout | undefined>();

  // ------------------- Fetch seniors and caregivers -----------------------
  const usersData = useFetchEntityUsers({
    entityId: opts.entityId,
    userType: UserType.Senior,
  });
  const caregiversData = useFetchEntityUsers({
    activeUsers: true,
    entityId: opts.entityId,
    userType: UserType.Caregiver,
  });

  const fetchRange = useMemo(() => {
    switch (opts.range) {
      case '1d':
        return [1, 'day'];
      case '1w':
      case '7d':
      case '15d':
        return [1, 'day'];
      case '1m':
      case '30d':
        return [1, 'day'];
      default:
        return [1, 'day'];
    }
  }, [opts.range]);

  const refresh = useCallback(() => {
    abort.current?.abort();
    const requestAbort = new AbortController();
    abort.current = requestAbort;
    const QUEUE_LIMIT = 6;
    let inQueue = 0;

    const fetchCaretasks = async (start: Moment, end: Moment): Promise<void> => {
      let startSliced = start.clone();
      let endSliced = startSliced.clone().add(fetchRange[0] as any, fetchRange[1]);
      if (endSliced.isAfter(end)) endSliced = end;

      // Increment queue
      inQueue++;
      setFetching(true);

      // Fetch Entity care tasks
      EntityUserCareTasksRepository.instance
        .GetEntityCareTasks(
          opts.entityId,
          startSliced.utc().toISOString(),
          endSliced.utc().toISOString(),
          {
            signal: abort.current?.signal,
          }
        )
        .then(async (response) => {
          // Get Entity Users info
          const usersIds = response.map((task) => task.careTask.userId);
          await storeEntityUsersInfo(usersIds);

          // Transform data to planner activity items
          const plannerData = transformEntityCareTasks2PlannerActivityItems(
            response,
            cacheUsers.current
          );

          // Update state
          setItems((prev) => {
            const newPlannerData = prev.concat(plannerData);
            // sort by date
            newPlannerData.sort((a, b) => a.date.obj.diff(b.date.obj));
            return newPlannerData;
          });
        })
        .catch((error: Error) => {
          if (error.name === 'CanceledError') return;

          snackBar.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.planner.errors.content),
          });
          console.error(error);
        })
        .finally(() => {
          // Decrement queue
          inQueue--;

          // Validate completion
          if (inQueue === 0) {
            // all fetching done
            setFetching(false);
          }
        });

      // Validate completion
      if (endSliced.isSameOrAfter(end)) {
        // all fetching done
        return;
      }

      // Validate queue capacity
      while (inQueue >= QUEUE_LIMIT) {
        // wait a bit & check again
        await wait(100);
      }

      // Queue not in max capacity, fetch next slice
      return fetchCaretasks(endSliced, end);
    };

    const fetchAnniversaries = async (start: Moment, end: Moment): Promise<void> => {
      // Fetch Entity Birthdays
      const birthdaysData = await EntitiesRepository.instance
        .getBirtdays(opts.entityId, start.utc().toISOString(), end.utc().toISOString(), {
          signal: abort.current?.signal,
        })
        .catch((error: Error) => {
          if (error.name === 'CanceledError') return undefined;

          // snackBar.setOfficeToastNotification({
          //   severity: 'error',
          //   message: t(translations.planner.errors.xxxxx),
          // });
          console.error(error);

          return undefined;
        });

      if (!birthdaysData) return;

      // Fetch Entity Users info
      const userIds = birthdaysData.map((user) => user.id!);
      await storeEntityUsersInfo(userIds);

      // Transform data to planner anniversary items
      const birthdaysItems = transformEntityAnniversaries2PlannerAnniversaryItems(
        birthdaysData,
        cacheUsers.current,
        start
      );

      // sort by date
      birthdaysItems.sort((a, b) => a.date.obj.diff(b.date.obj));

      if (birthdaysItems) {
        setItems((x) => x.concat(birthdaysItems));
      }
    };

    const fetchShiftAllocations = (): void => {
      // Increment queue
      inQueue++;
      setFetching(true);
      EntityShiftsAllocationsRepository.instance
        .getListFormatEntityShiftsAllocations(
          opts.entityId,
          opts.startDate.utc().toISOString(),
          opts.endDate.utc().toISOString(),
          undefined,
          {
            includePreviousDayStartedShifts: false,
            config: {signal: requestAbort.signal},
          }
        )
        .then((response) => {
          if (requestAbort.signal.aborted) return;
          setShiftAllocations(response);
        })
        .catch((error: Error) => {
          if (error.name === 'CanceledError') return;

          console.error(error);
        })
        .finally(() => {
          // Decrement queue
          inQueue--;

          // Validate completion
          if (inQueue === 0) {
            // all fetching done
            setFetching(false);
          }
        });
    };

    const storeEntityUsersInfo = async (usersIds: string[]): Promise<void> => {
      // Fetch users info
      const uniqueUsersIds = Array.from(new Set(usersIds));
      const newUsersIds = uniqueUsersIds.filter((userId) => !cacheUsers.current[userId]);

      // Get data for new users. Skip already cached users.
      for (const newUserId of newUsersIds) {
        try {
          // - Old way (deprecated to avoid multiple requests)
          // const userInfo = await EntityUsersRepository.instance.getById(opts.entityId, newUserId);
          // - New way
          // Get data from users data
          const userInfo = usersData.items?.find((user) => user.user.id === newUserId);

          // Validate
          if (!userInfo) {
            // Skip user
            console.debug(`User with id ${newUserId} not found`);
            continue;
          }

          // Store in Cache
          cacheUsers.current[newUserId] = userInfo;
        } catch (error) {
          // Skip user
        }
      }
    };

    try {
      // Reset state
      reset();
      // First fetch
      fetchCaretasks(opts.startDate, opts.endDate);
      fetchAnniversaries(opts.startDate, opts.endDate);
      fetchShiftAllocations();
    } catch (error) {
      // TODO - handle error
      console.log(error);
    }

    // Useffect Cleanup
    return () => {
      abort.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRange, opts.entityId, /*snackBar,*/ t, opts.startDate, opts.endDate, usersData.items]);

  const filterDataByActivitiesGroups = useCallback((filterGroupsIds: string[] | undefined) => {
    setFilterActivitiesGroups(filterGroupsIds);
  }, []);

  const filterDataByUser = useCallback((user: AffiliatedUser | undefined) => {
    setFilterUser(user);
  }, []);

  const filterDataByCaregiver = useCallback((caregiver: AffiliatedUser | undefined) => {
    setFilterCaregiver(caregiver);
  }, []);

  const filterDataByUserFilters = useCallback((filters: UserFilters) => {
    setFilters(filters);
  }, []);

  useEffect(() => {
    let result: PlannerItem[] = items;

    // --- FILTER BY USER ---
    if (filterUser !== undefined) {
      const newFilteredItems = result.filter((item) => {
        if (item.type === 'activity') {
          return item.users.some((user) => user.id === filterUser.user.id);
        }

        return true;
      });
      result = newFilteredItems;
    }

    // --- FILTER BY CAREGIVER ---
    if (filterCaregiver !== undefined) {
      const newFilteredItems = result.filter((item) => {
        if (item.type === 'activity') {
          const itemDay = item.date.obj.format('YYYY-MM-DD');
          const caregiverShiftIds = new Set(
            shiftAllocations
              ?.filter((allocation) => {
                const allocationDate = allocation.day.slice(0, 10); // 'YYYY-MM-DD' instead of 'YYYY-MM-DDTHH:mm:ss.sssZ'
                return allocation.userId === filterCaregiver.user.id && allocationDate === itemDay;
              })
              .map((allocation) => allocation.entityShiftId)
          );

          return item.users.some((user) =>
            user.careTask.rawData.careTask.entityShiftIds?.some((entityShiftId) =>
              caregiverShiftIds.has(entityShiftId)
            )
          );
        }

        return true;
      });
      result = newFilteredItems;
    }

    // --- FILTER BY ACTIVITIES GROUPS ---
    if (filterActivitiesGroups !== undefined) {
      const newFilteredItems = result.filter((item) => {
        if (item.type === 'activity') {
          // filterActivitiesGroups: The array to filter out(discard).
          return !filterActivitiesGroups?.includes(item.activitiesGroup.id);
        }

        if (item.type === 'anniversary') {
          return !filterActivitiesGroups?.includes(BIRTHDAY_SECTOR_ID);
        }

        return true;
      });
      result = newFilteredItems;
    }

    // --- FILTER BY SOCIAL RESPONSES ---
    if (filters.socialResponses !== undefined) {
      const newFilteredItems = result.filter((item) => {
        if (item.type === 'activity') {
          return filters.socialResponses?.some((socialResponse) =>
            item.users.some((user) => user.socialResponses?.includes(socialResponse))
          );
        }

        return true;
      });
      result = newFilteredItems;
    }

    // --- FILTER BY CARE TASK STATUS ---
    if (filters.taskStatus !== undefined) {
      const newFilteredItems = result.filter((item) => {
        if (item.type === 'activity') {
          return filters.taskStatus?.some((careTaskStatus) => {
            return item.users.some((user) => userFilterByCareTaskStatus(user, careTaskStatus));
          });
        }

        return true;
      });
      result = newFilteredItems;
    }

    // --- FILTER BY SHIFTS ---
    if (filters.shifts !== undefined) {
      const newFilteredItems = result.filter((item) => {
        if (item.type === 'activity') {
          return filters.shifts?.some((shiftId) => item.shiftIds?.includes(shiftId));
        }

        return true;
      });
      result = newFilteredItems;
    }

    // --- FILTER BY TASK GROUPS ---
    if (
      filters.badl !== undefined ||
      filters.iadl !== undefined ||
      filters.clinicalSupport !== undefined ||
      filters.personalDevelopment !== undefined
    ) {
      const newFilteredItems = result.filter((item) => {
        if (item.type === 'activity') {
          return (
            filters.badl?.some((taskGroup) => item.activitiesGroup.id?.includes(taskGroup)) ||
            filters.iadl?.some((taskGroup) => item.activitiesGroup.id?.includes(taskGroup)) ||
            filters.clinicalSupport?.some((taskGroup) =>
              item.activitiesGroup.id?.includes(taskGroup)
            ) ||
            filters.personalDevelopment?.some((taskGroup) =>
              item.activitiesGroup.id?.includes(taskGroup)
            )
          );
        }

        return true;
      });
      result = newFilteredItems;
    }

    // --- SET FILTERED ITEMS ---
    setFilteredItems(result);
  }, [items, filterActivitiesGroups, filterUser, filterCaregiver, filters, shiftAllocations]);

  const reset = () => {
    setFetching(true);
    setItems([]);
    setFilteredItems([]);
    setShiftAllocations(undefined);
  };

  useEffect(() => {
    if (usersData.isLoading) return;
    clearTimeout(timeout.current);
    abort.current?.abort();
    reset();
    timeout.current = setTimeout(() => refresh(), 500);

    return () => {
      clearTimeout(timeout.current);
      abort.current?.abort();
    };
  }, [refresh, usersData.isLoading]);

  return {
    items: filteredItems,
    usersData,
    caregiversData,
    fetching,
    refresh,
    filterDataByActivitiesGroups,
    filterDataByUser,
    filterDataByCaregiver,
    filterDataByUserFilters,
  };
}

function userFilterByCareTaskStatus(
  user: PlannerActivityUser,
  caretaskStatus: TaskStatus
): boolean {
  switch (caretaskStatus) {
    case 'planned':
      return !user.careTask.unattended && !user.careTask.handled;
    case 'handled':
      return user.careTask.handled;
    case 'not_done':
      return user.careTask.done === false;
    case 'done':
      return user.careTask.done === true;
    case 'not_handled':
      return user.careTask.unattended;
    default:
      return true;
  }
}
